import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { VerificationProvider, useVerification } from './contexts/VerificationContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary';

// Surface the supervisor no-delete rule clearly, whatever the page shows.
axios.interceptors.response.use(undefined, (error) => {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 403 && typeof detail === 'string' && detail.startsWith('Supervisors cannot delete')) {
    toast.error(detail, { id: 'supervisor-no-delete' });
  }
  return Promise.reject(error);
});
import IdleLogoutManager from './components/IdleLogoutManager';

import EmployeeDetailPage from './pages/admin/EmployeeDetailPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import UpcomingShiftsPage from './pages/admin/UpcomingShiftsPage';
import EventBoardPage from './pages/admin/EventBoardPage';
import ArchivedEventsPage from './pages/admin/ArchivedEventsPage';
import AdminAcademicBreaksPage from './pages/admin/AdminAcademicBreaksPage';
import SupervisorShiftsPage from './pages/supervisor/SupervisorShiftsPage';
import SupervisorEventsListPage from './pages/supervisor/SupervisorEventsListPage';
import SupervisorMyShiftsPage from './pages/supervisor/SupervisorMyShiftsPage';
import SupervisorMyHistoryPage from './pages/supervisor/SupervisorMyHistoryPage';
import SupervisorUpcomingShiftsPage from './pages/supervisor/SupervisorUpcomingShiftsPage';
import EmployeeAvailableShiftsPage from './pages/employee/EmployeeAvailableShiftsPage';
import HelpPage from './pages/shared/HelpPage';

// Terms Pages
import LegalAndPoliciesPage from './pages/LegalAndPoliciesPage';
import AdminLegalManagerPage from './pages/admin/LegalManagerPage';

// New Feature Pages
// import AdminPoliciesPage from './pages/admin/PoliciesPage';  // replaced by LegalAndPoliciesPage
import AdminBackupPage from './pages/admin/BackupPage';
import AdminAppLoginActivityPage from './pages/admin/AdminAppLoginActivityPage';
import AdminShiftAuditLogPage from './pages/admin/AdminShiftAuditLogPage';
import AdminRolesPage from './pages/admin/AdminRolesPage';
import AdminPolicyCompliancePage from './pages/admin/AdminPolicyCompliancePage';
import AdminOnboardingAuditPage from './pages/admin/AdminOnboardingAuditPage';
import AdminMandatoryTrainingPage from './pages/admin/AdminMandatoryTrainingPage';
import MandatoryTrainingPage from './pages/MandatoryTrainingPage';
import DocumentReviewPage from './pages/admin/DocumentReviewPage';
import SupervisorClockHistoryPage from './pages/supervisor/SupervisorClockHistoryPage';
import SupervisorTeamClockPage from './pages/supervisor/SupervisorTeamClockPage';
import SupervisorAcademicBreaksPage from './pages/supervisor/SupervisorAcademicBreaksPage';
import SupervisorTeamAcademicBreaksPage from './pages/supervisor/SupervisorTeamAcademicBreaksPage';

// Feedback Pages
import PublicFeedbackPage from './pages/shared/PublicFeedbackPage';
import PublicCardFeedbackPage from './pages/PublicCardFeedbackPage';
import PrintBadgePage from './pages/shared/PrintBadgePage';
import AdminFeedbackHubPage from './pages/admin/AdminFeedbackHubPage';
import GeneralFeedbackPage from './pages/shared/GeneralFeedbackPage';
import AdminFeedbackCardsPage from './pages/admin/AdminFeedbackCardsPage';
import QRAssignPage from './pages/admin/QRAssignPage';
import KitPage from './pages/admin/KitPage';
import PayrollPage from './pages/admin/PayrollPage';
import MyPayslipsPage from './pages/employee/MyPayslipsPage';
import MyKitPage from './pages/employee/MyKitPage';
import AdminMessagesPage from './pages/admin/AdminMessagesPage';
import AdminDepartmentPage from './pages/admin/AdminDepartmentPage';
import JobApplicationsPage from './pages/admin/JobApplicationsPage';
import HrOnboardingPage from './pages/employee/HrOnboardingPage';
import AdminClaimRequestsPage from './pages/admin/AdminClaimRequestsPage';
import ContactPage from './pages/ContactPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import ClientDashboardPage from './pages/client/ClientDashboardPage';
import ClientInviteAcceptPage from './pages/client/ClientInviteAcceptPage';
import ClientRequestsPage from './pages/client/ClientRequestsPage';
import ClientNewRequestPage from './pages/client/ClientNewRequestPage';
import ClientDocumentsPage from './pages/client/ClientDocumentsPage';
import ClientMessagesPage from './pages/client/ClientMessagesPage';
import AdminClientMessagesPage from './pages/admin/AdminClientMessagesPage';
import AdminClientDocumentsPage from './pages/admin/AdminClientDocumentsPage';
import AdminClientAuditPage from './pages/admin/AdminClientAuditPage';
import AdminStaffingRequestsPage from './pages/admin/AdminStaffingRequestsPage';
import AdminDeletionRequestsPage from './pages/admin/AdminDeletionRequestsPage';
import OpsDeletionRequestsPage from './pages/employee/OpsDeletionRequestsPage';
import CrMessagesPage from './pages/employee/CrMessagesPage';
import CrAnnouncementsPage from './pages/employee/CrAnnouncementsPage';
import ComplianceDashboardPage from './pages/admin/ComplianceDashboardPage';
import ComplianceQueuePage from './pages/admin/ComplianceQueuePage';

// Pages
import LoginPage from './pages/LoginPage';
import PublicFAQPage from './pages/PublicFAQPage';
import TermsToJoinPage from './pages/TermsToJoinPage';
import VerificationPage from './pages/VerificationPage';
import DashboardPage from './pages/admin/DashboardPage';
import EmployeesPage from './pages/admin/EmployeesPage';
import ShiftsPage from './pages/admin/ShiftsPage';
import SavedSitesPage from './pages/admin/SavedSitesPage';
import HolidayEntitlementPage from './pages/admin/HolidayEntitlementPage';
import BreaksPage from './pages/admin/BreaksPage';
import PayrollEmployeesPage from './pages/payroll/PayrollEmployeesPage';
import PayrollReportsPage from './pages/payroll/PayrollReportsPage';
import ClockHistoryPage from './pages/admin/ClockHistoryPage';
import NotificationsPage from './pages/admin/NotificationsPage';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import EmployeeShiftsPage from './pages/employee/EmployeeShiftsPage';
import EmployeeEventsListPage from './pages/employee/EmployeeEventsListPage';
import EmployeeArchivedEventsPage from './pages/employee/EmployeeArchivedEventsPage';
import EmployeeMessagesPage from './pages/employee/EmployeeMessagesPage';
import PayMessagesPage from './pages/employee/PayMessagesPage';
import HrMessagesPage from './pages/employee/HrMessagesPage';
import OpsMessagesPage from './pages/employee/OpsMessagesPage';
import EmployeeDutyReportsPage from './pages/employee/EmployeeDutyReportsPage';
import EmployeeSettingsPage from './pages/employee/EmployeeSettingsPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import AdminDutyReportsPage from './pages/admin/AdminDutyReportsPage';
import AdminMenuLayoutPage from './pages/admin/AdminMenuLayoutPage';
import AdminFAQPage from './pages/admin/AdminFAQPage';
import AnnouncementsPage from './pages/admin/AnnouncementsPage';
import EmployeeFAQPage from './pages/employee/EmployeeFAQPage';
import SupervisorFAQPage from './pages/supervisor/SupervisorFAQPage';
import SupervisorAnnouncementsPage from './pages/supervisor/SupervisorAnnouncementsPage';
import EmployeeAcademicBreaksPage from './pages/employee/EmployeeAcademicBreaksPage';
import EmployeeHistoryPage from './pages/employee/EmployeeHistoryPage';
import MyProfilePage from './pages/employee/MyProfilePage';
import SupervisorDashboard from './pages/supervisor/SupervisorDashboard';

// Protected Route Components
// Secondary-admin: supervisors may open every admin page EXCEPT these.
const SUPERVISOR_BLOCKED_PREFIXES = [
  '/admin/clients', '/admin/staffing-requests', '/admin/client-messages', '/admin/client-documents',
  '/admin/app-login-activity', '/admin/shift-audit-log', '/admin/onboarding-audit', '/admin/client-audit',
  '/admin/menu-layout',
];

function ProtectedRoute({ children, allowedRoles, allowWhileOnboarding = false, allowWhileVerifying = false, allowWhileMustChangePassword = false }) {
  const { user, loading } = useAuth();
  const { locked, loading: onboardingLoading } = useOnboarding();
  const { locked: verificationLocked, loading: verificationLoading } = useVerification();
  const location = useLocation();
  
  if (loading || onboardingLoading || verificationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Blocked admin areas apply to EVERY supervisor session, even on routes
  // that explicitly allow the supervisor role.
  if (user.role === 'supervisor'
    && SUPERVISOR_BLOCKED_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return <Navigate to="/supervisor" replace />;
  }

  const supervisorAsAdmin = user.role === 'supervisor'
    && Array.isArray(allowedRoles) && allowedRoles.includes('admin')
    && location.pathname !== '/admin'
    && !SUPERVISOR_BLOCKED_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  if (allowedRoles && !allowedRoles.includes(user.role) && !supervisorAsAdmin) {
    const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : user.role === 'client' ? '/client' : '/employee';
    return <Navigate to={redirectPath} replace />;
  }

  // Forced password change: users issued a temporary password (forgot-password
  // or admin reset) must set a new password before accessing anything. The
  // /change-password route itself is flagged `allowWhileMustChangePassword`.
  if (user.must_change_password && !allowWhileMustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }
  
  // Verification gate: users with an unverified email must complete
  // email verification BEFORE any onboarding step. Routes explicitly
  // flagged `allowWhileVerifying` (currently only /verify itself) stay
  // open — everything else redirects.
  if (verificationLocked && !allowWhileVerifying) {
    return <Navigate to="/verify" replace />;
  }
  
  // Onboarding gate: new/reactivated employees + supervisors are locked out
  // of every operational route until profile + docs + policies + terms are
  // all complete. Routes explicitly flagged `allowWhileOnboarding` (My
  // Profile, Documents-inside-profile, Legal & Policies) stay open.
  if (locked && !allowWhileOnboarding) {
    const redirectPath = user.role === 'supervisor' ? '/supervisor/my-profile' : '/employee/my-profile';
    return <Navigate to={redirectPath} replace />;
  }
  
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    const redirectPath = user.role === 'admin' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : user.role === 'client' ? '/client' : '/employee';
    return <Navigate to={redirectPath} replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/feedback" element={<GeneralFeedbackPage />} />
      <Route path="/feedback/card/:cardCode" element={<PublicCardFeedbackPage />} />
      <Route path="/feedback/:code" element={<PublicFeedbackPage />} />
      <Route path="/print-badge" element={<PrintBadgePage />} />
      <Route path="/terms-to-join" element={<TermsToJoinPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/client-invite/:token" element={<ClientInviteAcceptPage />} />
      <Route path="/client" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/client/requests" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientRequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/client/requests/new" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientNewRequestPage />
        </ProtectedRoute>
      } />
      <Route path="/client/documents" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/client/messages" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/client-messages" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminClientMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/client-documents" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminClientDocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/client-audit" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminClientAuditPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/staffing-requests" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminStaffingRequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/deletion-requests" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDeletionRequestsPage />
        </ProtectedRoute>
      } />
      {/* OPERATIONS department — employee portal + scheduling-only access */}
      <Route path="/employee/ops/events" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ShiftsPage category="event" opsMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/ops/retail" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ShiftsPage category="retail" opsMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/ops/events-manager" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <UpcomingShiftsPage opsMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/ops/staffing" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <AdminStaffingRequestsPage opsMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/ops/deletion-requests" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <OpsDeletionRequestsPage />
        </ProtectedRoute>
      } />
      {/* CONTROL ROOM department — employee portal + narrow operational access */}
      <Route path="/employee/cr/events" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ShiftsPage category="event" crMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/cr/retail" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ShiftsPage category="retail" crMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/cr/messages" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <CrMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/cr/announcements" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <CrAnnouncementsPage />
        </ProtectedRoute>
      } />
      {/* PAYROLL department — employee portal + read-only clock history + payslip uploads */}
      <Route path="/employee/pay/clock-history" element={<Navigate to="/employee/pay/clock-history/retail" replace />} />
      <Route path="/employee/pay/clock-history/retail" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ClockHistoryPage payrollMode category="retail" />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/clock-history/event" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ClockHistoryPage payrollMode category="event" />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/clock-history/all" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ClockHistoryPage payrollMode combined />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/holiday" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <HolidayEntitlementPage payrollMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/breaks" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <BreaksPage payrollMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/employees" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <PayrollEmployeesPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/reports" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <PayrollReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/payslips" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <PayrollPage payrollMode />
        </ProtectedRoute>
      } />
      {/* HR & COMPLIANCE department — employee portal + compliance systems */}
      <Route path="/employee/hr/compliance" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ComplianceDashboardPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/compliance-queue" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <ComplianceQueuePage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/document-review" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <DocumentReviewPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/academic-breaks" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <AdminAcademicBreaksPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/legal" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <AdminLegalManagerPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/policy-compliance" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <AdminPolicyCompliancePage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/mandatory-training" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <AdminMandatoryTrainingPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/job-applications" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <JobApplicationsPage hrMode />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/onboarding" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <HrOnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/verify" element={
        <ProtectedRoute allowWhileOnboarding allowWhileVerifying>
          <VerificationPage />
        </ProtectedRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/faq" element={<PublicFAQPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/employees" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EmployeesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/departments/:slug" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDepartmentPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/employees/:employeeId" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EmployeeDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/shifts" element={<Navigate to="/admin/shifts/events" replace />} />
      <Route path="/admin/shifts/events" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ShiftsPage category="event" />
        </ProtectedRoute>
      } />
      <Route path="/admin/shifts/retail" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ShiftsPage category="retail" />
        </ProtectedRoute>
      } />
      <Route path="/admin/shifts/sites" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <SavedSitesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clock-history" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ClockHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clock-history/retail" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ClockHistoryPage category="retail" />
        </ProtectedRoute>
      } />
      <Route path="/admin/clock-history/event" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ClockHistoryPage category="event" />
        </ProtectedRoute>
      } />
      <Route path="/admin/clock-history/payroll" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ClockHistoryPage combined />
        </ProtectedRoute>
      } />
      <Route path="/admin/notifications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/upcoming-shifts" element={<Navigate to="/admin/shifts/events" replace />} />
      <Route path="/admin/events/create" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <UpcomingShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/events/view" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <UpcomingShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/event-board/:dayId" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <EventBoardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/archived-events" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ArchivedEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/academic-breaks" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAcademicBreaksPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/help" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/terms" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LegalAndPoliciesPage role="admin" />
        </ProtectedRoute>
      } />
      <Route path="/admin/policies" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LegalAndPoliciesPage role="admin" />
        </ProtectedRoute>
      } />
      <Route path="/admin/legal" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LegalAndPoliciesPage role="admin" />
        </ProtectedRoute>
      } />
      <Route path="/admin/legal/manage" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminLegalManagerPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/legal/:section" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LegalAndPoliciesPage role="admin" />
        </ProtectedRoute>
      } />
      <Route path="/admin/backup" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminBackupPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/app-login-activity" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAppLoginActivityPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/shift-audit-log" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminShiftAuditLogPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/roles" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminRolesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/policy-compliance" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminPolicyCompliancePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/onboarding-audit" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminOnboardingAuditPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/mandatory-training" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminMandatoryTrainingPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/mandatory-training" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <MandatoryTrainingPage role="employee" />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/mandatory-training" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <MandatoryTrainingPage role="supervisor" />
        </ProtectedRoute>
      } />
      <Route path="/admin/document-review" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DocumentReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminFeedbackHubPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/public-feedback" element={<Navigate to="/admin/feedback?tab=staff" replace />} />
      <Route path="/admin/feedback-cards" element={<Navigate to="/admin/feedback?tab=cards" replace />} />
      <Route path="/supervisor/feedback-cards" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <AdminFeedbackHubPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/qr-assign" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <QRAssignPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/kit" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <KitPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payroll" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <PayrollPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payroll/holiday" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <HolidayEntitlementPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payroll/breaks" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <BreaksPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/payslips" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <MyPayslipsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/kit" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <MyKitPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/qr-assign" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <QRAssignPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/kit" element={
        <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
          <KitPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/swap-requests" element={<Navigate to="/admin" replace />} />
      <Route path="/admin/claim-requests" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminClaimRequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/claim-requests" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <AdminClaimRequestsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/job-applications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <JobApplicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/compliance" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ComplianceDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/compliance-queue" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ComplianceQueuePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-changes" element={<Navigate to="/admin" replace />} />
      
      {/* Employee Routes */}
      <Route path="/employee" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeDashboard />
        </ProtectedRoute>
      } />
      <Route path="/employee/shifts" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/academic-breaks" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeAcademicBreaksPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/transport" element={<Navigate to="/employee" replace />} />
      <Route path="/employee/available-shifts" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeAvailableShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/events-list" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeEventsListPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/archived-events" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeArchivedEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/messages" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/pay/messages" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <PayMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/hr/messages" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <HrMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/ops/messages" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <OpsMessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/change-password" element={
        <ProtectedRoute allowWhileMustChangePassword={true} allowWhileVerifying={true} allowWhileOnboarding={true}>
          <ForceChangePasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/settings" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/faq" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminFAQPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AnnouncementsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/faq" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeFAQPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/faq" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorFAQPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/announcements" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorAnnouncementsPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/duty-reports" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeDutyReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/duty-reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDutyReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/menu-layout" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminMenuLayoutPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/history" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <EmployeeHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/my-profile" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <MyProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/employee/help" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/employee/terms" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="employee" />
        </ProtectedRoute>
      } />
      <Route path="/employee/legal" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="employee" />
        </ProtectedRoute>
      } />
      <Route path="/employee/legal/:section" element={
        <ProtectedRoute allowedRoles={['employee']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="employee" />
        </ProtectedRoute>
      } />
      <Route path="/employee/shift-swap" element={<Navigate to="/employee" replace />} />
      
      {/* Supervisor Routes */}
      <Route path="/supervisor/my-profile" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <MyProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/available-shifts" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <EmployeeAvailableShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/shift-swap" element={<Navigate to="/supervisor" replace />} />
      <Route path="/supervisor" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/shifts" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/events-list" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorEventsListPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/transport" element={<Navigate to="/supervisor" replace />} />
      <Route path="/supervisor/my-shifts" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorMyShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/my-transport" element={<Navigate to="/supervisor" replace />} />
      <Route path="/supervisor/my-history" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorMyHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/upcoming-shifts" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorUpcomingShiftsPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/help" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/terms" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="supervisor" />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/legal" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="supervisor" />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/legal/:section" element={
        <ProtectedRoute allowedRoles={['supervisor']} allowWhileOnboarding>
          <LegalAndPoliciesPage role="supervisor" />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/clock-history" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorClockHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/team-clock" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorTeamClockPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/academic-breaks" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorAcademicBreaksPage />
        </ProtectedRoute>
      } />
      <Route path="/supervisor/team-academic-breaks" element={
        <ProtectedRoute allowedRoles={['supervisor']}>
          <SupervisorTeamAcademicBreaksPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  // Native date/time pickers: on dark theme the browser's picker icon is
  // nearly invisible and taps only select text segments (no calendar/clock
  // on mobile). Any click on a date/time input now opens the native picker.
  useEffect(() => {
    const openPicker = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT'
        && ['date', 'time', 'datetime-local', 'month', 'week'].includes(t.type)
        && !t.disabled && !t.readOnly) {
        try { t.showPicker?.(); } catch (_) { /* unsupported / already open */ }
      }
    };
    document.addEventListener('click', openPicker);
    return () => document.removeEventListener('click', openPicker);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <VerificationProvider>
            <OnboardingProvider>
              <NotificationProvider>
                <BrowserRouter>
                  <AppRoutes />
                  <IdleLogoutManager />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </NotificationProvider>
            </OnboardingProvider>
          </VerificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
